/**
 * The booking being put together: where from and to, when, who's going and how they'll
 * pay. Kept in sessionStorage so a refresh (or a trip to the payment page) doesn't lose it.
 */
import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import type { PaymentMethod, Place, Quote } from "./types";

export type When = { asap: true } | { asap: false; date: string; time: string };

export type Draft = {
  pickup: Place | null;
  dropoff: Place | null;
  stops: Place[];
  when: When;
  passengers: number;
  quotes: Quote[];
  quoteId: string | null;
  paymentMethod: PaymentMethod | null;
  notes: string;
  passengerName: string;
  luggage: number;
  accountReference: string;
};

export const emptyDraft = (): Draft => ({
  pickup: null,
  dropoff: null,
  stops: [],
  when: { asap: true },
  passengers: 1,
  quotes: [],
  quoteId: null,
  paymentMethod: null,
  notes: "",
  passengerName: "",
  luggage: 0,
  accountReference: "",
});

const KEY = "draft";

function stored(): Draft {
  try {
    const value = sessionStorage.getItem(KEY);
    return value ? { ...emptyDraft(), ...(JSON.parse(value) as Partial<Draft>) } : emptyDraft();
  } catch {
    return emptyDraft();
  }
}

type DraftContextValue = {
  draft: Draft;
  update: (changes: Partial<Draft>) => void;
  reset: (start?: Partial<Draft>) => void;
};

const DraftContext = createContext<DraftContextValue | null>(null);

export function DraftProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<Draft>(stored);
  const save = useCallback((next: Draft) => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Private browsing: it just won't survive a refresh.
    }
    return next;
  }, []);
  const update = useCallback(
    (changes: Partial<Draft>) => setDraft((current) => save({ ...current, ...changes })),
    [save],
  );
  const reset = useCallback(
    (start: Partial<Draft> = {}) => setDraft(save({ ...emptyDraft(), ...start })),
    [save],
  );
  return <DraftContext.Provider value={{ draft, update, reset }}>{children}</DraftContext.Provider>;
}

export function useDraft(): DraftContextValue {
  const value = useContext(DraftContext);
  if (!value) throw new Error("useDraft outside DraftProvider");
  return value;
}

/** Places used lately on this phone, for the suggestions. */
const RECENT_KEY = "recentPlaces";

export function recentPlaces(): Place[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as Place[];
  } catch {
    return [];
  }
}

/** On signing out: the places were this customer's. */
export function forgetRecentPlaces() {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    // Storage is off: nothing kept.
  }
}

export function rememberPlace(place: Place) {
  try {
    const others = recentPlaces().filter(
      (p) => !(Math.abs(p.lat - place.lat) < 1e-5 && Math.abs(p.lng - place.lng) < 1e-5),
    );
    localStorage.setItem(RECENT_KEY, JSON.stringify([place, ...others].slice(0, 6)));
  } catch {
    // Storage is off: no recent places.
  }
}

export const placeTitle = (place: Place) =>
  place.name || place.address.split(",")[0] || place.address;

export function placeSubtitle(place: Place): string {
  if (place.name) return place.address;
  const rest = place.address.split(",").slice(1).join(",").trim();
  return rest || place.postcode || "";
}
